import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Home, ChevronRight, TrendingUp } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

/**
 * Mobile My Deals Tile Component
 * Shows summary stats and milestone dots for top active deals
 */
const MobileMyDealsTile = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getToken } = useClerkAuth();
  const navigate = useNavigate();
  const backendUrl = API_BASE_URL;

  useEffect(() => {
    fetchAllDeals();
  }, []);

  const fetchAllDeals = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await axios.get(`${backendUrl}/api/pnl/all-deals`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  // Split deals
  const todayStr = new Date().toISOString().slice(0, 10);
  const activeDeals = deals.filter(d => d.closing_date >= todayStr);
  const closedDeals = deals.filter(d => d.closing_date < todayStr);
  const totalCommission = deals.reduce((sum, d) => sum + (d.final_income || 0), 0);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Render milestone dots for a deal
  const renderMilestoneDots = (deal) => {
    const milestones = deal.milestones || [false, false, false, false, false, false, false];
    return (
      <div className="flex gap-0.5">
        {milestones.map((checked, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full ${checked ? 'bg-primary' : 'bg-gray-200'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Home className="w-5 h-5 text-primary" />
            My Deals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="mb-4 cursor-pointer hover:shadow-md transition-shadow" 
      onClick={() => navigate('/dashboard?panel=mydeals')}
      data-testid="my-deals-tile"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Home className="w-5 h-5 text-primary" />
            My Deals
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">{activeDeals.length}</div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{closedDeals.length}</div>
            <div className="text-xs text-gray-500">Closed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalCommission)}</div>
            <div className="text-xs text-gray-500">Earned</div>
          </div>
        </div>

        {/* Top 2 active deals with milestone dots */}
        {activeDeals.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            {activeDeals.slice(0, 2).map(deal => (
              <div key={deal.id} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{deal.house_address}</div>
                  <div className="text-xs text-gray-500">Closing: {deal.closing_date ? new Date(deal.closing_date + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : 'TBD'}</div>
                </div>
                {renderMilestoneDots(deal)}
              </div>
            ))}
            {activeDeals.length > 2 && (
              <p className="text-xs text-gray-500 text-center mt-1">
                +{activeDeals.length - 2} more active deals
              </p>
            )}
          </div>
        )}

        {activeDeals.length === 0 && closedDeals.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-2">
            No deals yet. Tap to add your first deal.
          </div>
        )}

        {/* View All link */}
        <div className="mt-3 pt-3 border-t text-center">
          <span className="text-sm font-medium text-primary flex items-center justify-center gap-1">
            View All Deals
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default MobileMyDealsTile;
